"use client";

import { Suspense, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, useForm } from "react-hook-form";
import { addDays, format, getDay, isValid } from "date-fns";
import { BOOKING_MIN_LEAD_DAYS, BOOKING_WINDOWS } from "@/constants/dates";
import { ComponentHeader, PhoneInputField } from "@/components/ui";
import { useSearchParams } from "next/navigation";
import { ReceiptPreview } from "./ReceiptPreview";
import { BookingStepper } from "./BookingStepper";
import { PAGE_COPY } from "@/constants/pages";
import { BOOKING_FORM } from "@/constants/booking-form";
import { API } from "@/constants/api";
import { type BookingClientValues, bookingClientSchema } from "@/utils/booking";
import { getBrowserTimeZone } from "@/utils/timeZone";
import type { BookingCurrency, Service } from "@/types";

type TimeSlot = {
  start: string;
  end: string;
  available: boolean;
};

type BookingPageClientProps = {
  services: BookingPageService[];
  currency: BookingCurrency;
  defaultCountryCode?: string;
};

type BookingPageService = Pick<Service, "id" | "name" | "price"> & {
  sessionLength: number;
};

const formatSlotTime = (time24h: string) => {
  const [hours, minutes] = time24h.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time24h;
  const meridiem = hours >= 12 ? "PM" : "AM";
  const normalizedHour = hours % 12 || 12;
  return `${normalizedHour}:${String(minutes).padStart(2, "0")} ${meridiem}`;
};

const getInitialBookingDate = () => addDays(new Date(), BOOKING_MIN_LEAD_DAYS);

// --- MAIN PAGE ---
function BookingPageContent({ services, currency, defaultCountryCode }: BookingPageClientProps) {
  const bookingCopy = PAGE_COPY.booking.page;

  const searchParams = useSearchParams();
  const serviceId = searchParams.get(bookingCopy.serviceParamKey);
  const service = serviceId ? (services.find((item) => item.id === serviceId) ?? null) : null;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isStartingBooking, setIsStartingBooking] = useState(false);
  const [availability, setAvailability] = useState<TimeSlot[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [minimumBookingDate] = useState(getInitialBookingDate);
  const [userTimeZone] = useState(getBrowserTimeZone);
  const {
    control,
    getValues,
    register,
    trigger,
    watch,
    formState: { errors },
  } = useForm<BookingClientValues>({
    resolver: zodResolver(bookingClientSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      date: minimumBookingDate,
    },
  });
  const name = watch("name");
  const phone = watch("phone");
  const email = watch("email");
  const date = watch("date");

  // FETCH AVAILABILITY ON DATE CHANGE
  useEffect(() => {
    setSelectedSlot(null);

    if (!date || !isValid(date) || !service || !userTimeZone) {
      setAvailability([]);
      setAvailabilityLoading(false);
      return;
    }

    let isMounted = true;

    const fetchAvailability = async () => {
      const formattedDate = format(date, bookingCopy.bookingDateFormat);
      const params = new URLSearchParams({
        [API.availability.queryKeys.date]: formattedDate,
        [API.availability.queryKeys.sessionLength]: String(service.sessionLength),
        [API.availability.queryKeys.timeZone]: userTimeZone,
      });

      setAvailabilityLoading(true);
      try {
        const res = await fetch(`${bookingCopy.api.availability}?${params.toString()}`);
        const data = await res.json();
        if (isMounted) {
          setAvailability(data.slots || []);
        }
      } catch {
        if (isMounted) {
          setAvailability([]);
        }
      } finally {
        if (isMounted) {
          setAvailabilityLoading(false);
        }
      }
    };

    void fetchAvailability();

    return () => {
      isMounted = false;
    };
  }, [bookingCopy, date, service, userTimeZone]);

  const hasAvailableSlots = availability.some((slot) => slot.available);

  const handleSelectSlot = async (slot: TimeSlot) => {
    if (!slot.available || !date || !isValid(date) || !service || !serviceId) return;

    setSelectedSlot(slot);
    setErrorMessage(null);

    const isFormValid = await trigger();

    if (!isFormValid) {
      return;
    }

    if (typeof grecaptcha === "undefined") {
      setErrorMessage(BOOKING_FORM.errors.verificationFailedRefresh);
      return;
    }

    setIsStartingBooking(true);

    try {
      const token = await grecaptcha.execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!, {
        action: BOOKING_FORM.recaptcha.action,
      });

      if (!token) {
        setErrorMessage(BOOKING_FORM.errors.securityCheckFailed);
        return;
      }

      const values = getValues();
      const formattedDate = format(values.date, bookingCopy.bookingDateFormat);

      const res = await fetch(bookingCopy.api.initiateBooking, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          [BOOKING_FORM.recaptcha.headerName]: token,
        },
        body: JSON.stringify({
          serviceId,
          name: values.name,
          email: values.email,
          phone: values.phone,
          date: formattedDate,
          start: slot.start,
          end: slot.end,
          timeZone: userTimeZone,
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        if (data?.error === API.initiateBooking.errors.botDetected) {
          setErrorMessage(BOOKING_FORM.errors.securityCheckFailed);
        } else {
          setErrorMessage(BOOKING_FORM.errors.startFailed);
        }

        return;
      }

      window.location.assign(bookingCopy.verificationPath);
    } catch (error) {
      console.error(error);
      setErrorMessage(BOOKING_FORM.errors.startFailed);
    } finally {
      setIsStartingBooking(false);
    }
  };

  if (!service) {
    return <Typography color="error">{bookingCopy.noServiceFallback}</Typography>;
  }

  return (
    <Container maxWidth="xl" sx={{ my: { xs: 6, md: 2 } }}>
      <Box p={4}>
        <ComponentHeader
          title={`${bookingCopy.titlePrefix} ${service.name}`}
          subTitle={bookingCopy.subtitle}
        />
        <BookingStepper activeStep={0} />

        <Grid container spacing={4}>
          {/* LEFT — FORM */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label={bookingCopy.labels.name}
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
                placeholder={bookingCopy.placeholders.name}
                required
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInputField
                    label={bookingCopy.labels.phone}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    defaultCountryCode={defaultCountryCode}
                    required
                  />
                )}
              />

              <TextField
                fullWidth
                label={bookingCopy.labels.email}
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
                placeholder={bookingCopy.placeholders.email}
                required
              />

              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    open={isDatePickerOpen}
                    label={bookingCopy.labels.date}
                    value={field.value}
                    onChange={field.onChange}
                    onOpen={() => setIsDatePickerOpen(true)}
                    onClose={() => setIsDatePickerOpen(false)}
                    minDate={minimumBookingDate}
                    maxDate={addDays(new Date(), bookingCopy.maxBookingDaysAhead)}
                    shouldDisableDate={(day) => BOOKING_WINDOWS[getDay(day)] == null}
                    disablePast
                    format={bookingCopy.displayDateFormat}
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.date,
                        helperText: errors.date?.message,
                        onClick: () => setIsDatePickerOpen(true),
                        onKeyDown: (event) => {
                          if (
                            event.key === "Enter" ||
                            event.key === " " ||
                            event.key === "ArrowDown"
                          ) {
                            event.preventDefault();
                            setIsDatePickerOpen(true);
                          }
                        },
                        sx: {
                          "& .MuiInputBase-root": {
                            cursor: "pointer",
                          },
                          "& input": {
                            cursor: "pointer",
                          },
                        },
                      },
                    }}
                  />
                )}
              />

              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

              <Box mt={4}>
                <Typography variant="h6" mb={2}>
                  {bookingCopy.labels.availableSlots}
                </Typography>

                {availabilityLoading ? (
                  <Typography color="text.secondary">{bookingCopy.availabilityLoading}</Typography>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {availability.map((slot) => (
                        <Grid key={slot.start} size={{ xs: 6, sm: 6, md: 4 }}>
                          <Button
                            fullWidth
                            variant={slot.available ? "contained" : "outlined"}
                            disabled={!slot.available || isStartingBooking}
                            onClick={() => handleSelectSlot(slot)}
                            sx={{
                              minHeight: 84,
                              borderRadius: 1.25,
                              px: 2,
                              py: 1.25,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                              textTransform: "none",
                              transition:
                                "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                              ...(slot.available
                                ? {
                                    "&:hover": {
                                      transform: "translateY(-2px)",
                                      boxShadow: (theme) => theme.shadows[6],
                                      backgroundColor: "primary.dark",
                                    },
                                    "&:active": {
                                      transform: "translateY(0)",
                                    },
                                  }
                                : {
                                    "&.Mui-disabled": {
                                      borderColor: "divider",
                                    },
                                  }),
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                fontSize: { xs: "0.98rem", sm: "1.08rem" },
                                lineHeight: 1.2,
                                color: slot.available ? "primary.contrastText" : "text.disabled",
                              }}
                            >
                              {formatSlotTime(slot.start)} - {formatSlotTime(slot.end)}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.78rem",
                                letterSpacing: 0.2,
                                color: slot.available ? "primary.contrastText" : "text.disabled",
                                opacity: slot.available ? 0.9 : 1,
                              }}
                            >
                              {service.sessionLength} {bookingCopy.sessionLabelSuffix}
                            </Typography>
                          </Button>
                        </Grid>
                      ))}
                    </Grid>

                    {!hasAvailableSlots && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        <AlertTitle>{bookingCopy.noAvailableSlotsTitle}</AlertTitle>
                        {bookingCopy.noAvailableSlotsDescriptionPrefix}{" "}
                        {date
                          ? format(date, bookingCopy.unavailableDateFormat)
                          : bookingCopy.noAvailableSlotsFallbackDate}
                        . {bookingCopy.noAvailableSlotsDescriptionSuffix}
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            </Stack>
          </Grid>

          {/* RIGHT — LIVE RECEIPT */}
          <Grid size={{ xs: 12, md: 6 }}>
            <ReceiptPreview
              name={name}
              email={email}
              phone={phone}
              service={service}
              currency={currency}
              date={date}
              slot={selectedSlot}
            />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default function BookingPage({
  services,
  currency,
  defaultCountryCode,
}: BookingPageClientProps) {
  return (
    <Suspense fallback={<Typography>{PAGE_COPY.booking.loadingText}</Typography>}>
      <BookingPageContent
        services={services}
        currency={currency}
        defaultCountryCode={defaultCountryCode}
      />
    </Suspense>
  );
}
